/**
 * Drawer — slide-in navigation / side panel (ADR-114).
 *
 * Sides: left (navigation), right (detail panels), bottom (mobile actions —
 * equivalent to a sheet). Includes overlay backdrop, focus trap, ESC and
 * scroll-lock registration.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { SHEET_MAX_HEIGHT_CLASS } from './constants';
import { useOverlayRegistration } from './OverlayProvider';
import { useFocusTrap } from './useFocusTrap';

export type DrawerSide = 'left' | 'right' | 'bottom';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  side?: DrawerSide;
  /** Panel width class for left/right sides (default `w-[85%] max-w-[320px]`). */
  width?: string;
  className?: string;
  ariaLabel?: string;
  showClose?: boolean;
  closeLabel?: string;
  dismissible?: boolean;
  'data-testid'?: string;
}

export function Drawer({
  open,
  onClose,
  children,
  side = 'left',
  width = 'w-[85%] max-w-[320px]',
  className,
  ariaLabel,
  showClose = true,
  closeLabel = 'Close',
  dismissible = true,
  'data-testid': testId = 'drawer-panel',
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useOverlayRegistration(open);
  useFocusTrap(open, panelRef);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, dismissible, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const isBottom = side === 'bottom';

  const positionClasses = isBottom
    ? cn('inset-x-0 bottom-0 w-full rounded-t-2xl', SHEET_MAX_HEIGHT_CLASS)
    : side === 'left'
      ? cn('inset-y-0 left-0 h-full', width)
      : cn('inset-y-0 right-0 h-full', width);

  const content = (
    <div className="fixed inset-0 z-50" data-drawer-side={side}>
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => dismissible && onClose()}
        aria-hidden="true"
        data-testid="drawer-backdrop"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={cn(
          'absolute flex flex-col overflow-y-auto bg-white shadow-xl outline-none dark:bg-gray-900 dark:text-gray-100',
          positionClasses,
          className,
        )}
        data-testid={testId}
      >
        {children}
        {showClose && dismissible && (
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'absolute top-4 rounded-lg p-2 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-gray-800',
              isBottom ? 'right-4' : side === 'left' ? 'right-4' : 'left-4',
            )}
            aria-label={closeLabel}
            data-testid="drawer-close"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
